<template>
  <b-container fluid>
    <b-row>
      <b-col class="p-3 mr-5">
        <paywall-form-input
          size="lg"
          :config="config"
          :device="device"
          :placeholder="config.header"
          :char-max="config.rules.header.charMax"
          label="header"
          config-key="header"
          required />
        <paywall-form-input
          :config="config"
          :device="device"
          :placeholder="config.subheader"
          :char-max="config.rules.subheader.charMax"
          label="subheader"
          config-key="subheader" />
      </b-col>

      <b-col class=" p-3 ml-5">
        <div>
          <p
            class="ml-3"
            style="margin-bottom: -7px">
            Packages
          </p>
          <div role="tablist">
            <b-container
              v-if="config.packages.length < 1"
              style="height: 10vh; font-size: larger;"
              class="text-secondary text-capitalize d-flex justify-content-center">
              <b-card-text class="align-self-center font-weight-bold">
                minimum of {{ min }} package is required
              </b-card-text>
            </b-container>

            <draggable
              v-model="config.packages"
              :group="`${deviceID}-pkg`"
              handle=".drag-title">
              <transition-group>
                <b-card
                  v-for="(pkgItem,i) in config.packages"
                  :key="`${deviceID}-${i}-pkg`"
                  class="m-3"
                  no-body>
                  <b-card-header
                    header-tag="header"
                    class="p-2 d-flex drag-title"
                    role="tab">
                    <b-icon
                      icon="grip-vertical"
                      class="align-self-center" />
                    <div
                      v-b-toggle="`${deviceID}-${i}-pkg`"
                      class="pl-1 w-100 d-flex justify-content-between align-items-center"
                      @click.prevent="pkgOpen = pkgOpen === i ? null : i">
                      {{ pkgItem.title ? pkgItem.title : "(Blank Package Title)" }}
                      <b-icon
                        :icon="i === pkgOpen ? `chevron-down` : `chevron-right`"
                        class="align-self-center ml-auto"
                        variant="secondary"
                        scale="1.2" />
                    </div>
                  </b-card-header>
                  <b-collapse
                    :id="`${deviceID}-${i}-pkg`"
                    :accordion="`${deviceID}-pkg`"
                    role="tabpanel">
                    <b-card-body>
                      <b-form-group>
                        <b-checkbox
                          v-model="pkgItem.enabled"
                          switch>
                          Enabled
                        </b-checkbox>
                      </b-form-group>
                      <b-container v-if="pkgItem.enabled">
                        <paywall-dropdown
                          required
                          class="text-capitalize"
                          label="type"
                          :config="pkgItem"
                          config-key="type" />

                        <paywall-form-input
                          required
                          :device="device"
                          :config="pkgItem"
                          :char-max="config.rules.title.charMax"
                          label="title"
                          config-key="title" />
                        <paywall-form-input
                          required
                          :device="device"
                          :config="pkgItem"
                          :char-max="config.rules.price.charMax"
                          label="price"
                          config-key="price" />
                        <paywall-form-input
                          required
                          :device="device"
                          :config="pkgItem"
                          :char-max="config.rules.text.charMax"
                          label="textarea"
                          config-key="text" />
                        <paywall-form-input
                          :device="device"
                          :config="pkgItem"
                          :placeholder="pkgItem.purchaseLink"
                          :char-max="config.rules.purchaseLink.charMax"
                          label="purchaseLink"
                          config-key="purchaseLink" />

                        <paywall-form-package-buttons
                          :device="device"
                          :pkg-buttons="pkgItem" />
                      </b-container>
                    </b-card-body>
                    <b-button
                      v-if="pkgItem.enabled"
                      class="ml-4 mb-3"
                      variant="danger"
                      @click="onDeletePkg(i)">
                      Delete
                    </b-button>
                  </b-collapse>
                </b-card>
              </transition-group>
            </draggable>

            <b-button
              :disabled="disableAdd"
              class="mt-3 ml-3"
              variant="success"
              @click="addPkg">
              Add Package
            </b-button>
          </div>

          <paywall-overlay
            :show="showOverlay"
            :msg="overlayMsg"
            @onConfirm="onConfirm"
            @onCancel="onCancel" />
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import PaywallFormInput from "@/components/Paywall/PaywallFormInput"
import PaywallDropdown from "@/components/Paywall/PaywallDropdown"
import PaywallOverlay from "@/components/Paywall/PaywallOverlay"
import PaywallFormPackageButtons from "@/components/Paywall/PaywallFormPackageButtons"
import draggable from "vuedraggable"

export default {
  name: "PaywallFormPackages",
  components: {PaywallOverlay, PaywallDropdown, PaywallFormInput, PaywallFormPackageButtons, draggable},
  props: {
    config: {
      type: Object,
      default: null,
      required: true,
    },
    configKey: {
      type: String,
      default: null,
      required: true,
    },
    device: {
      type: String,
      default: null,
      required: true,
    },
    placeholder: {
      type: Object,
      default: null,
      required: true,
    },
  },
  data() {
    return {
      min: "1",
      max: "4",
      showOverlay: false,
      pkgIndexToDelete: null,
      disableAdd: false,
      pkgOpen: null,
    }
  },
  computed: {
    deviceID() {
      return this.device.replaceAll(" ", '')
    },
    overlayMsg() {
      const title = this.pkgIndexToDelete === null ? '' : `${this.config.packages[this.pkgIndexToDelete].title}`
      return `delete ${this.device}'s ${title} package?`
    },
  },
  methods: {
    onConfirm() {
      if (this.showOverlay && this.pkgIndexToDelete !== null) {
        this.config.packages.splice(this.pkgIndexToDelete, 1)
        this.showOverlay = !this.showOverlay
        this.pkgIndexToDelete = null

        if (this.config.packages.length < this.max) {
          this.disableAdd = false
        }
      }
    },
    onCancel() {
      this.showOverlay = !this.showOverlay
      this.pkgIndexToDelete = null
    },
    addPkg() {
      if (this.config.packages.length + 1 <= this.max) {
        this.config.packages = [...this.config.packages, this.placeholder.packages[0]]
      } else {
        this.disableAdd = true
        this.$bvToast.toast(`${this.device} supports a maximum of ${this.max} packages`, {
          title: "Cannot Add Package",
          variant: "danger",
          bodyClass: "text-capitalize text-lg",
        })
      }
    },
    onDeletePkg(idx) {
      this.showOverlay = true
      this.pkgIndexToDelete = idx
    },
  },
}
</script>

<style scoped>
.drag-title {
  border: 1px solid transparent;
}

.drag-title:hover > div > .bi-chevron-right {
  animation: 0.75s infinite ease-in-out alternate b-icon-animation-cylon;
}

.drag-title:hover {
  border: 1px solid var(--primary);
  transition: all 50ms ease;
  cursor: grab;
}

.drag-title:active, .drag-title:focus {
  cursor: grabbing;
  color: white;
  background: darkgrey;
  transform: scale(1.06);
  transition: transform 50ms ease;
}
</style>